import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { ArrowRight } from "lucide-react";
import { Layout } from "@/components/site/Layout";
import { Button } from "@/components/ui/button";
import { FAQ } from "@/components/site/article/FAQ";

const SITE = "https://xauproacademy.com";
const PAGE_URL = `${SITE}/what-is-xauusd`;

const FAQS = [
  { q: "What is XAUUSD?", a: "XAUUSD is the spot price of one troy ounce of gold quoted in US dollars. If XAUUSD shows 2,400.00, one troy ounce of gold costs 2,400 US dollars." },
  { q: "Why does the gold symbol start with X?", a: "Under the ISO 4217 currency standard, codes for assets that are not issued by a national government begin with X. XAU combines X with AU, the chemical symbol for gold (from the Latin aurum). Silver follows the same rule as XAG." },
  { q: "What is the trading symbol for gold?", a: "It depends on the market. Spot gold against the dollar is XAUUSD. CME gold futures use GC (100 troy ounces) and micro gold futures use MGC (10 troy ounces). Some brokers display spot gold as GOLD." },
  { q: "How much is one lot of XAUUSD?", a: "At most retail brokers one standard lot of XAUUSD equals 100 troy ounces, so a 1 dollar move in price changes the position value by 100 dollars. Contract sizes vary by broker, so confirm the specification before trading." },
  { q: "What is a pip in gold?", a: "There is no single universal definition. Many brokers quote XAUUSD to two decimals and call 0.01 a point and 0.10 a pip, while others use 0.01 as the pip. Measuring risk in dollars per ounce avoids this ambiguity." },
  { q: "Is XAUUSD the same as gold futures?", a: "No. XAUUSD is a spot or CFD quote, while GC and MGC are exchange-traded futures with fixed contract sizes, expiry months and exchange margin. Prices are related but not identical because futures include carry costs." },
];

const STRUCTURE = [
  { id: "x", code: "X", label: "Non-national asset prefix", body: "ISO 4217 reserves codes beginning with X for supranational units and commodities, such as gold, silver, platinum and the IMF Special Drawing Right (XDR)." },
  { id: "au", code: "AU", label: "Chemical symbol for gold", body: "Au comes from the Latin aurum. Together, XAU identifies one troy ounce of gold as a currency-like unit." },
  { id: "usd", code: "USD", label: "Quote currency", body: "The second code is the currency used to price the base unit. XAUUSD therefore reads as the number of US dollars per troy ounce." },
] as const;

const CONTRACTS = [
  { name: "Standard lot", oz: 100, venue: "Spot / CFD" },
  { name: "Mini lot", oz: 10, venue: "Spot / CFD" },
  { name: "Micro lot", oz: 1, venue: "Spot / CFD" },
  { name: "GC future", oz: 100, venue: "CME COMEX" },
  { name: "MGC future", oz: 10, venue: "CME COMEX" },
] as const;

export const Route = createFileRoute("/what-is-xauusd")({
  head: () => ({
    meta: [
      { title: "What Is XAUUSD? Gold Trading Symbol Explained | XAU Pro Academy" },
      { name: "description", content: "What XAUUSD means, why the gold symbol starts with X under ISO 4217, gold futures symbols GC and MGC, lot sizes in ounces and how pip value works in gold." },
      { property: "og:title", content: "What Is XAUUSD? The Gold Trading Symbol Explained" },
      { property: "og:description", content: "ISO 4217 code XAU, spot vs futures symbols, 100, 10 and 1 ounce contracts, and gold pip math in one clear reference." },
      { property: "og:type", content: "article" },
      { property: "og:url", content: PAGE_URL },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: PAGE_URL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "DefinedTermSet",
          name: "Gold trading symbols",
          url: PAGE_URL,
          hasDefinedTerm: [
            { "@type": "DefinedTerm", name: "XAUUSD", description: "Spot price of one troy ounce of gold quoted in US dollars.", inDefinedTermSet: PAGE_URL },
            { "@type": "DefinedTerm", name: "XAU", description: "ISO 4217 code for one troy ounce of gold.", inDefinedTermSet: PAGE_URL },
            { "@type": "DefinedTerm", name: "Troy ounce", description: "Precious metals weight unit equal to 31.1035 grams.", inDefinedTermSet: PAGE_URL },
          ],
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQS.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: SITE },
            { "@type": "ListItem", position: 2, name: "What Is XAUUSD", item: PAGE_URL },
          ],
        }),
      },
    ],
  }),
  component: WhatIsXauusdPage,
});

const SECTIONS = [
  ["definition", "Definition"],
  ["symbol", "Symbol anatomy"],
  ["markets", "Spot vs futures"],
  ["contracts", "Contract sizes"],
  ["pips", "Pip math"],
  ["faq", "FAQ"],
] as const;

function SymbolDiagram() {
  const [active, setActive] = useState<(typeof STRUCTURE)[number]["id"]>("x");
  const item = STRUCTURE.find((s) => s.id === active)!;
  const boxes = [
    { id: "x", x: 40, w: 90, text: "X" },
    { id: "au", x: 140, w: 140, text: "AU" },
    { id: "usd", x: 320, w: 200, text: "USD" },
  ] as const;
  return (
    <figure className="rounded-lg border border-border bg-card p-5">
      <svg viewBox="0 0 560 200" role="img" aria-labelledby="sym-t sym-d" className="w-full">
        <title id="sym-t">Anatomy of the XAUUSD symbol</title>
        <desc id="sym-d">XAUUSD splits into X, the ISO 4217 non-national prefix, AU, the chemical symbol for gold, and USD, the quote currency.</desc>
        {boxes.map((b) => (
          <g key={b.id} className="cursor-pointer" onClick={() => setActive(b.id)}>
            <rect x={b.x} y={40} width={b.w} height={90} rx={6}
              className={active === b.id ? "fill-gold/15 stroke-gold" : "fill-transparent stroke-border"} strokeWidth={2} />
            <text x={b.x + b.w / 2} y={102} textAnchor="middle" className={active === b.id ? "fill-gold" : "fill-foreground"} style={{ font: "600 48px var(--font-mono, monospace)" }}>{b.text}</text>
          </g>
        ))}
        <line x1={40} y1={160} x2={280} y2={160} className="stroke-gold" strokeWidth={1.5} />
        <text x={160} y={184} textAnchor="middle" className="fill-muted-foreground" style={{ font: "12px monospace" }}>BASE: 1 TROY OZ GOLD</text>
        <line x1={320} y1={160} x2={520} y2={160} className="stroke-muted-foreground" strokeWidth={1.5} />
        <text x={420} y={184} textAnchor="middle" className="fill-muted-foreground" style={{ font: "12px monospace" }}>QUOTE: US DOLLARS</text>
      </svg>
      <div className="mt-4 flex flex-wrap gap-2" role="group" aria-label="Symbol part">
        {STRUCTURE.map((s) => (
          <Button key={s.id} size="sm" variant={active === s.id ? "gold" : "outline"} aria-pressed={active === s.id} onClick={() => setActive(s.id)}>{s.code}</Button>
        ))}
      </div>
      <figcaption className="mt-4 text-sm text-muted-foreground" aria-live="polite">
        <span className="font-semibold text-foreground">{item.code}: {item.label}.</span> {item.body}
      </figcaption>
    </figure>
  );
}

function ContractDiagram() {
  const [move, setMove] = useState(5);
  const max = 100;
  return (
    <figure className="rounded-lg border border-border bg-card p-5">
      <svg viewBox="0 0 560 230" role="img" aria-labelledby="ct-t ct-d" className="w-full">
        <title id="ct-t">Gold contract sizes in troy ounces</title>
        <desc id="ct-d">Bars compare 100, 10 and 1 ounce spot lots with GC and MGC futures, and show dollar change for the selected price move.</desc>
        {CONTRACTS.map((c, i) => {
          const y = 14 + i * 42;
          const w = Math.max(4, (c.oz / max) * 300);
          return (
            <g key={c.name}>
              <text x={0} y={y + 18} className="fill-foreground" style={{ font: "13px sans-serif" }}>{c.name}</text>
              <rect x={110} y={y + 4} width={w} height={20} rx={3} className={c.venue === "CME COMEX" ? "fill-muted-foreground/50" : "fill-gold/70"} />
              <text x={120 + w} y={y + 19} className="fill-muted-foreground" style={{ font: "12px monospace" }}>{c.oz} oz</text>
              <text x={555} y={y + 19} textAnchor="end" className="fill-gold" style={{ font: "600 13px monospace" }}>${(c.oz * move).toLocaleString("en-US")}</text>
            </g>
          );
        })}
      </svg>
      <label htmlFor="move" className="mt-4 block text-sm text-muted-foreground">Price move: <span className="font-mono text-foreground">${move} per ounce</span></label>
      <input id="move" type="range" min={1} max={50} value={move} onChange={(e) => setMove(Number(e.target.value))} className="mt-2 w-full accent-gold" />
      <figcaption className="mt-3 text-xs text-muted-foreground">Value change = ounces × price move. Gold bars represent common broker lots; grey bars are CME futures. Broker specifications vary.</figcaption>
    </figure>
  );
}

function PipTable() {
  return (
    <div className="overflow-x-auto rounded-lg border border-border">
      <table className="w-full text-sm">
        <thead className="bg-muted/40 text-left font-mono text-xs uppercase tracking-wider text-muted-foreground">
          <tr><th className="p-3">Price change</th><th className="p-3">Common label</th><th className="p-3">1 standard lot (100 oz)</th><th className="p-3">1 micro lot (1 oz)</th></tr>
        </thead>
        <tbody className="divide-y divide-border">
          {[["0.01", "Point (or pip at some brokers)", "$1", "$0.01"], ["0.10", "Pip at many brokers", "$10", "$0.10"], ["1.00", "One dollar move", "$100", "$1"], ["10.00", "Ten dollar move", "$1,000", "$10"]].map((r) => (
            <tr key={r[0]}>{r.map((c, i) => <td key={i} className={`p-3 ${i === 0 || i > 1 ? "font-mono" : ""}`}>{c}</td>)}</tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function WhatIsXauusdPage() {
  return (
    <Layout>
      <header className="border-b border-border bg-slate-deep">
        <div className="container mx-auto max-w-4xl px-4 py-16">
          <p className="font-mono text-xs uppercase tracking-widest text-gold">Gold fundamentals · Reference</p>
          <h1 className="mt-4 font-display text-4xl font-bold md:text-5xl">What Is XAUUSD? The Gold Trading Symbol Explained</h1>
          <p className="mt-5 text-lg text-muted-foreground">
            <strong className="text-foreground">XAUUSD is the price of one troy ounce of gold in US dollars.</strong> XAU is the ISO 4217 code for gold, and USD is the currency it is priced in. This reference explains the symbol, the contracts that track it and how price moves translate into money.
          </p>
        </div>
      </header>

      <nav aria-label="In this guide" className="sticky top-16 z-30 border-b border-border bg-background/90 backdrop-blur-xl">
        <div className="container mx-auto flex max-w-4xl gap-5 overflow-x-auto px-4 py-3 text-sm">
          {SECTIONS.map(([id, label]) => <a key={id} href={`#${id}`} className="whitespace-nowrap text-muted-foreground hover:text-gold">{label}</a>)}
        </div>
      </nav>

      <article className="container mx-auto max-w-4xl space-y-16 px-4 py-14 leading-relaxed">
        <section id="definition" className="scroll-mt-32">
          <h2 className="font-display text-3xl font-bold">XAUUSD definition</h2>
          <p className="mt-4 text-muted-foreground">XAUUSD is a currency-style pair. The first unit (XAU, one troy ounce of gold) is the base; the second (USD) is the quote. A quote of 2,400.50 means one ounce costs 2,400.50 US dollars. When the price rises, gold is gaining against the dollar; when it falls, the dollar is gaining against gold.</p>
          <dl className="mt-6 grid gap-4 sm:grid-cols-3">
            {[["Base unit", "1 troy oz = 31.1035 g"], ["Quote", "US dollars"], ["Market hours", "Near 24 hours, Monday to Friday"]].map(([k, v]) => (
              <div key={k} className="rounded-md border border-border p-4"><dt className="font-mono text-xs uppercase text-muted-foreground">{k}</dt><dd className="mt-1 font-semibold">{v}</dd></div>
            ))}
          </dl>
        </section>

        <section id="symbol" className="scroll-mt-32">
          <h2 className="font-display text-3xl font-bold">Why the gold symbol starts with X</h2>
          <p className="mt-4 mb-6 text-muted-foreground">ISO 4217 is the international standard for currency codes. National currencies use the country code plus a letter (USD, GBP, JPY). Assets without an issuing country receive an X prefix. Select each part of the symbol to read its meaning.</p>
          <SymbolDiagram />
          <p className="mt-4 text-sm text-muted-foreground">Related codes: XAG (silver), XPT (platinum), XPD (palladium).</p>
        </section>

        <section id="markets" className="scroll-mt-32">
          <h2 className="font-display text-3xl font-bold">XAUUSD vs gold futures symbols</h2>
          <div className="mt-6 overflow-x-auto rounded-lg border border-border">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-left font-mono text-xs uppercase tracking-wider text-muted-foreground"><tr><th className="p-3">Symbol</th><th className="p-3">Instrument</th><th className="p-3">Where</th><th className="p-3">Size</th></tr></thead>
              <tbody className="divide-y divide-border">
                {[["XAUUSD / GOLD", "Spot gold or CFD", "Brokers (OTC)", "Broker defined, often 100 oz per lot"], ["GC", "Gold futures", "CME COMEX", "100 oz"], ["MGC", "Micro gold futures", "CME COMEX", "10 oz"], ["1OZ", "1-ounce gold futures", "CME COMEX", "1 oz"]].map((r) => (
                  <tr key={r[0]}>{r.map((c, i) => <td key={i} className={`p-3 ${i === 0 ? "font-mono text-gold" : ""}`}>{c}</td>)}</tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-4 text-muted-foreground">Spot and futures prices move together but differ by a small premium reflecting interest and storage costs. Futures expire on fixed months; spot CFDs roll daily with swap charges. US residents face different access rules, explained in our <Link to="/xauusd-trading/usa" className="text-gold hover:underline">US gold trading guide</Link>.</p>
        </section>

        <section id="contracts" className="scroll-mt-32">
          <h2 className="font-display text-3xl font-bold">100, 10 and 1 ounce: avoiding contract confusion</h2>
          <p className="mt-4 mb-6 text-muted-foreground">The most common sizing error is assuming a lot means the same thing everywhere. A one dollar move is worth 100 dollars on a standard lot but only 1 dollar on a micro lot. Move the slider to compare.</p>
          <ContractDiagram />
        </section>

        <section id="pips" className="scroll-mt-32">
          <h2 className="font-display text-3xl font-bold">Gold pip value vs forex pips</h2>
          <p className="mt-4 mb-6 text-muted-foreground">In EURUSD a pip is the fourth decimal (0.0001). Gold is quoted to two decimals, and brokers disagree on whether 0.01 or 0.10 is a pip. Institutional desks avoid the term and measure distance in dollars per ounce.</p>
          <PipTable />
          <div className="mt-6 flex flex-wrap gap-3">
            <Button asChild variant="gold"><Link to="/position-size-calculator">Calculate gold lot size <ArrowRight className="ml-1 h-4 w-4" /></Link></Button>
            <Button asChild variant="outline"><Link to="/xauusd-trading">How to trade gold</Link></Button>
            <Button asChild variant="outline"><Link to="/gold-trading-strategy">Gold trading strategy</Link></Button>
            <Button asChild variant="outline"><Link to="/gold-trading-sessions">Gold trading hours</Link></Button>
          </div>
        </section>

        <FAQ items={FAQS} />

        <p className="border-t border-border pt-6 text-xs text-muted-foreground">Educational content only, not financial advice. Contract specifications are illustrative and vary by broker and exchange; confirm with your provider. Trading leveraged gold products carries a high risk of loss.</p>
      </article>
    </Layout>
  );
}
